'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface PathwayOption {
  id: string;
  title: string;
  description: string;
  icon: string;
  revenue: string;
  benefits: string[];
  cta: string;
}

const ServicePathwaySelector = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedPathway, setSelectedPathway] = useState<string>('growth');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const pathways: PathwayOption[] = [
    {
      id: 'growth',
      title: 'Growth-Stage Business',
      description: 'Perfect for companies ready to scale with modern marketing and automation',
      icon: 'ChartBarIcon',
      revenue: '$1M - $50M Revenue',
      benefits: [
        'Marketing automation setup',
        'AI-powered customer insights',
        'Process optimization',
        'Scalable growth strategies'
      ],
      cta: 'Explore Growth Solutions'
    },
    {
      id: 'enterprise',
      title: 'Enterprise Innovation',
      description: 'Comprehensive digital transformation for established organizations',
      icon: 'BuildingOfficeIcon',
      revenue: '$50M+ Revenue',
      benefits: [
        'Enterprise AI integration',
        'Legacy system modernization',
        'Advanced analytics platforms',
        'Change management support'
      ],
      cta: 'View Enterprise Services'
    },
    {
      id: 'startup',
      title: 'Startup Acceleration',
      description: 'Strategic guidance for tech-savvy entrepreneurs building the future',
      icon: 'RocketLaunchIcon',
      revenue: 'Pre-seed to Series A',
      benefits: [
        'MVP automation strategy',
        'Cost-effective AI implementation',
        'Growth hacking techniques',
        'Investor-ready metrics'
      ],
      cta: 'Start Your Journey'
    }
  ];

  const selectedData = pathways.find(p => p.id === selectedPathway) || pathways[0];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Choose Your <span className="text-accent">Transformation Path</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Every business is unique. Select your business stage to discover tailored solutions that match your specific needs and growth ambitions.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {pathways.map((pathway) => (
            <button
              key={pathway.id}
              onClick={() => isHydrated && setSelectedPathway(pathway.id)}
              className={`p-6 rounded-xl text-left transition-all duration-300 ${
                selectedPathway === pathway.id
                  ? 'bg-primary text-primary-foreground shadow-elevation scale-105'
                  : 'bg-card hover:bg-muted border border-border'
              }`}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-3 rounded-lg ${
                  selectedPathway === pathway.id ? 'bg-accent' : 'bg-primary'
                }`}>
                  <Icon name={pathway.icon as any} size={24} className="text-primary-foreground" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-lg">{pathway.title}</h3>
                  <p className={`text-sm ${
                    selectedPathway === pathway.id ? 'text-primary-foreground/80' : 'text-muted-foreground'
                  }`}>
                    {pathway.revenue}
                  </p>
                </div>
              </div>
              <p className={`text-sm ${
                selectedPathway === pathway.id ? 'text-primary-foreground/90' : 'text-foreground'
              }`}>
                {pathway.description}
              </p>
            </button>
          ))}
        </div>

        {isHydrated && (
          <div className="bg-card rounded-2xl p-8 lg:p-12 border border-border shadow-subtle">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full">
                  <Icon name="CheckBadgeIcon" size={20} className="text-accent" />
                  <span className="text-sm font-medium text-accent">Recommended for You</span>
                </div>

                <h3 className="text-3xl font-display font-bold text-foreground">
                  {selectedData.title}
                </h3>

                <p className="text-lg text-muted-foreground">
                  {selectedData.description}
                </p>

                <div className="space-y-3">
                  {selectedData.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-success/10 flex items-center justify-center mt-0.5">
                        <Icon name="CheckIcon" size={16} className="text-success" />
                      </div>
                      <span className="text-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link
                    href="/services"
                    className="inline-flex items-center justify-center px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent/90 transition-all duration-300 shadow-subtle group"
                  >
                    {selectedData.cta}
                    <Icon name="ArrowRightIcon" size={18} className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-8 border border-border">
                  <div className="space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-border">
                      <span className="text-sm font-medium text-muted-foreground">Service Metrics</span>
                      <span className="text-xs text-success font-medium">Live Data</span>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-foreground">Implementation Speed</span>
                        <span className="text-2xl font-display font-bold text-success">4-6 weeks</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div className="bg-success h-2 rounded-full" style={{ width: '75%' }}></div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-foreground">Client Satisfaction</span>
                        <span className="text-2xl font-display font-bold text-brand-purple">94%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div className="bg-brand-purple h-2 rounded-full" style={{ width: '94%' }}></div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-foreground">Active Projects</span>
                        <span className="text-2xl font-display font-bold text-accent">150+</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div className="bg-accent h-2 rounded-full" style={{ width: '80%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicePathwaySelector;